import { useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check active session on initial load
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setUser(session.user);
        }
      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
      }
    );

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signInWithAzure = async () => {
    try {
      console.log('[OAuth/Start] Iniciando login com Azure...', {
        origin: window.location.origin,
        redirectTo: `${window.location.origin}/auth/callback`,
      });
      
      // ✅ Configuração com scopes específicos para obter email
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: 'openid email profile',
          queryParams: {
            prompt: 'select_account'
          }
        }
      });
      
      if (error) {
        console.error('[OAuth/Start] Azure OAuth error:', error);
        throw error;
      }
      
      console.log('[OAuth/Start] Azure authentication initiated successfully', {
        providerUrl: data?.url?.split('?')[0],
      });
    } catch (error) {
      console.error('[OAuth/Start] Unexpected Azure OAuth error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      console.log('🚪 SignOut: Starting logout process...');
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('❌ SignOut: Error during logout:', error);
        throw error;
      }
      console.log('✅ SignOut: Logout completed successfully');
    } catch (error) {
      console.error('❌ SignOut: Unexpected error during logout:', error);
      throw error;
    }
  };

  const uploadAvatar = async (file: File) => {
    if (!user) throw new Error('No user logged in');

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, { upsert: true });

    if (error) throw error;

    const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
    const avatarUrl = data.publicUrl;

    // Update user's profile with the new avatar URL
    const { error: updateError } = await supabase
      .from('users')
      .update({ avatar_url: avatarUrl })
      .eq('id', user.id);

    if (updateError) throw updateError;

    return avatarUrl;
  };

  return {
    user,
    loading,
    signInWithAzure,
    signOut,
    uploadAvatar,
  };
} 